import { Divider, Chip } from "@mui/material"
import {
    People,
    Event,
    LocationOn,
    AccessTime,
    CalendarMonth,
    Alarm,
    ConfirmationNumber,
    AdminPanelSettings
} from "@mui/icons-material"
import { EVENT_TYPES, EVENT_STATUS } from "../../../hooks/event-hooks/useEventStatus"
import { formatEventDate } from "../../../utils/dateFormatter"
import { H5, H4, Body2 } from "../../../themes/typography"

export default function AttendanceHome({
    data,
    formatEventDates,
    formatEventTiming,
    countdown,
    isCountdownActive,
    eventStatus
}) {

    const hasEventSchedules = data?.event?.event_schedules?.length > 0;

    const isSingleDayWithSlots =
        data?.event?.event_type === EVENT_TYPES.singleDay &&
        hasEventSchedules &&
        data?.event?.event_schedules.some(schedule => schedule.type === "slot");

    const isMultiDayWithDates =
        data?.event?.event_type === EVENT_TYPES.multiDay &&
        hasEventSchedules &&
        data?.event?.event_schedules.some(schedule => schedule.type === "date");

    const shouldShowEventSchedule = isSingleDayWithSlots || isMultiDayWithDates;

    // console.log("isCountdownActive -> ", isCountdownActive);
    // console.log("Countdown -> ", countdown);
    // console.log("eventStatus -> ", eventStatus);

    return (
        <div className="event-attendance-home-container">

            {/* Attendance Taking Person */}
            <div className="attendance-taking-person-container">

                <div className="attendance-taking-person">
                    <AdminPanelSettings color="tertiary" />
                    <H5>
                        Authorized Scanner
                    </H5>
                </div>

                <div className="attendance-taking-person-greeting">
                    <H4>
                        Welcome, <span>{data?.scanner?.name}</span>
                    </H4>
                </div>

                {data?.scanner?.scanner_id === data?.scanner?.creator_id && (
                    <div className="attendance-taking-person-greeting">
                        <Body2>
                            You are the event creator
                        </Body2>
                    </div>
                )}

                {data?.scanner?.scanner_id !== data?.scanner?.creator_id && (
                    <div className="attendance-taking-person-greeting">
                        <Body2>
                            Access granted by - {data?.scanner?.granted_by}
                        </Body2>
                    </div>
                )}

                {data?.scanner?.scanner_id !== data?.scanner?.creator_id && (
                    <div className="each-event-detail">
                        <div className="each-event-detail-icon">
                            <People color="white" sx={{ fontSize: '1rem' }} />
                        </div>
                        <div className="each-event-detail-info">
                            <Body2 fontWeight={500}>
                                Role: Event Staff
                            </Body2>
                        </div>
                    </div>
                )}

                {data?.scanner?.scanner_id === data?.scanner?.creator_id && (
                    <div className="each-event-detail">
                        <div className="each-event-detail-icon">
                            <People color="white" sx={{ fontSize: '1rem' }} />
                        </div>
                        <div className="each-event-detail-info">
                            <Body2 fontWeight={500}>
                                Role: Event Creator
                            </Body2>
                        </div>
                    </div>
                )}

            </div>


            {/* Event Details */}
            <div className="attendance-taking-person-container">

                <div className="attendance-taking-person">
                    <Event color="tertiary" />
                    <H5>
                        Event Details
                    </H5>
                </div>

                <div className="attendance-taking-person-greeting">
                    <H4 fontWeight={800} color="var(--secondary-color)">
                        {data?.event?.title}
                    </H4>
                </div>

                <div className="attendance-event-details">

                    <div className="each-event-detail">
                        <div className="each-event-detail-icon">
                            <CalendarMonth color="white" sx={{ fontSize: '1rem' }} />
                        </div>
                        <div className="each-event-detail-info">
                            <Body2 fontWeight={500}>
                                {formatEventDates}
                            </Body2>
                        </div>
                    </div>

                    <div className="each-event-detail">
                        <div className="each-event-detail-icon">
                            <AccessTime color="white" sx={{ fontSize: '1rem' }} />
                        </div>
                        <div className="each-event-detail-info">
                            <Body2 fontWeight={500}>
                                {formatEventTiming}
                            </Body2>
                        </div>
                    </div>

                    <div className="each-event-detail">
                        <div className="each-event-detail-icon">
                            <LocationOn color="white" sx={{ fontSize: '1rem' }} />
                        </div>
                        <div className="each-event-detail-info">
                            <Body2 fontWeight={500}>
                                {data?.event?.venue}
                            </Body2>
                        </div>
                    </div>

                </div>

                {/* Divider */}
                <Divider sx={{ borderColor: 'var(--divider-color)', alignSelf: 'stretch', margin: '0.25rem 0rem' }} />


                {/* Scanner Status */}
                <div className="scanner-status-container">

                    <div className="event-scanner-status">
                        <H5>
                            Scanner Status
                        </H5>

                        {(isCountdownActive === 'on' || isCountdownActive === 'windowEnded') && (
                            <Chip
                                label='Inactive'
                                color="danger"
                            />
                        )}

                        {isCountdownActive === 'off' && (
                            <Chip
                                label='Ready to Scan'
                                color="success"
                            />
                        )}

                    </div>

                    <div className="each-event-detail">
                        <div className="each-event-detail-icon">
                            <Alarm color="white" sx={{ fontSize: '1rem' }} />
                        </div>
                        <div className="each-event-detail-info">

                            {isCountdownActive === 'on' && eventStatus?.isEventScheduled && (
                                <Body2 fontWeight={500}>
                                    {countdown} left to scan
                                </Body2>
                            )}

                            {isCountdownActive === 'windowEnded' && eventStatus?.isEventDrafted && (
                                <Body2 fontWeight={500}>
                                    Event is drafted. Scanning will be available once the event is published.
                                </Body2>
                            )}

                            {isCountdownActive === 'windowEnded' && eventStatus?.isEventCancelled && (
                                <Body2 fontWeight={500}>
                                    Event is cancelled. Scanning is not available.
                                </Body2>
                            )}

                            {isCountdownActive === 'off' && eventStatus?.isEventLive && (
                                <Body2 fontWeight={500}>
                                    Event is live. Scanning is active.
                                </Body2>
                            )}

                        </div>
                    </div>

                </div>

            </div>

            {(eventStatus?.isEventLive || eventStatus?.isEventScheduled) && (
                <div className="attendance-taking-person-container">

                    <div className="attendance-taking-person">
                        <ConfirmationNumber color="tertiary" />
                        <H5>
                            Scanning Permissions
                        </H5>
                    </div>


                    {data?.permissions?.length === 0 && (
                        <div className="attendance-taking-person-greeting">
                            <Body2>
                                You do not have permission to scan any tickets. Please contact the event organizer.
                            </Body2>
                        </div>
                    )}

                    {data?.permissions?.length > 0 && (
                        <div className="attendance-taking-person-greeting">
                            <Body2>
                                You are authorized to scan these ticket types:
                            </Body2>
                        </div>
                    )}

                    {data?.permissions?.length > 0 && (
                        <div className="manual-search-instructions">
                            <ol>
                                {data?.permissions?.map((permission) => (
                                    <Body2 key={permission.id} fontWeight={350}>
                                        <li>
                                            {permission.title}
                                        </li>
                                    </Body2>
                                ))}
                            </ol>
                        </div>
                    )}

                </div>
            )}

            {(eventStatus?.isEventLive || eventStatus?.isEventScheduled) && (
                <>
                    {shouldShowEventSchedule && (
                        <div className="attendance-taking-person-container">

                            <div className="attendance-taking-person">
                                <CalendarMonth color="tertiary" />
                                <H5>
                                    Event Schedule
                                </H5>
                            </div>

                            {data?.event?.event_schedules?.map((schedule, index) => {
                                // Show only dates (type: "date")
                                if (schedule.type !== "date") return null;

                                // Find slots for this date if available (assuming slots will have parent_date_id = schedule.id)
                                const slots = data?.event?.event_schedules?.filter(
                                    (s) => s.type === "slot" && s.parent_date_id === schedule.id
                                );

                                return (
                                    <div key={schedule.id} className="attendance-schedule-items">
                                        {/* Always show the date */}
                                        {/* <div className="attendance-taking-person-greeting">
                                    <Body2>{index + 1}. {formatEventDate(schedule.date)} - {schedule.from_time} to {schedule.to_time}</Body2>
                                </div> */}

                                        {data?.event?.event_type === EVENT_TYPES.multiDay && (
                                            <div className="attendance-taking-person-greeting">
                                                <Body2>{index + 1}. {formatEventDate(schedule.date)} - {schedule.from_time} to {schedule.to_time}</Body2>
                                            </div>
                                        )}

                                        {data?.event?.event_type === EVENT_TYPES.singleDay && slots.length > 0 && (
                                            <div className="attendance-taking-person-greeting">
                                                <Body2>{formatEventDate(schedule.date)} - {schedule.from_time} to {schedule.to_time}</Body2>
                                            </div>
                                        )}

                                        {/* Render slots if exist */}
                                        {slots && slots.length > 0 && (
                                            <ol>
                                                {slots.map((slot, index) => (
                                                    <Body2 key={slot.id} fontWeight={350}>
                                                        <li>Slot {index + 1}: {slot.from_time} - {slot.to_time}</li>
                                                    </Body2>
                                                ))}
                                            </ol>
                                        )}
                                    </div>
                                );
                            })}

                        </div>
                    )}
                </>
            )}


        </div>
    )
}